import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { demeterNewsApi } from '../../../Apis/Apis';
import {
    DemeterNewsArticleModel,
    DemeterNewsArticleRatingModel,
    DemeterNewsArticleStatus,
    ListDemeterNewsArticlesResponse,
    SortDirection,
    UpdateDemeterNewsArticleStatusRequest,
} from '../../../Generated/Raven-Demeter';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import TableSkeleton from '../../Components/Layout/TableSkeleton';
import ActionsCellButton from '../../Components/Tables/ActionsCellButton/ActionsCellButton';
import Table from '../../Components/Tables/Table/Table';
import { TableHeader } from '../../Components/Tables/Table/TableDefinitions';
import { ApiListResponse } from '../../Components/Tables/TableWrapper/ApiListResponse';
import PaginationTableWrapper from '../../Components/Tables/TableWrapper/PaginationTableWrapper';
import useLanguage from '../../Services/Language/useLanguageHook';
import { ACTION_STATUS_MAP, ArticleAction, getAvailableActions, STATUS_LABELS } from './articleActions';
import ArticleDetail from './ArticleDetail';
import styles from './ArtificialIntelligence.module.scss';

const formatArticleDate = (row: DemeterNewsArticleModel): JSX.Element | string => {
    const dateString = row.articleDate;
    if (!dateString) {
        return '-';
    }
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? dateString : date.toLocaleString();
};

const renderStatusBadge = (row: DemeterNewsArticleModel): JSX.Element | string => {
    const label = STATUS_LABELS[row.articleStatus] ?? row.articleStatus;
    return <span className={styles.status_badge}>{label}</span>;
};

interface IArticleActionsCellProps {
    article: DemeterNewsArticleModel;
    onUpdated: () => void;
}

const ArticleActionsCell: React.FC<IArticleActionsCellProps> = (props) => {
    const availableActions = getAvailableActions(props.article.articleStatus);

    const [statusUpdating, updateStatus] = useApiWithoutAutoExecute<UpdateDemeterNewsArticleStatusRequest, object>(
        (request) => {
            if (!request) {
                return null;
            }
            return demeterNewsApi.updateDemeterNewsArticleStatus(request.demeterNewsArticleGuid, request);
        },
        { successMessage: 'Status updated', errorMessage: 'Failed to update status' },
    );

    const [ratingLoading, triggerRate] = useApiWithoutAutoExecute<void, object>(
        () => demeterNewsApi.runDemeterNewsArticleRating(props.article.demeterNewsArticleGuid),
        { successMessage: 'Rating complete', errorMessage: 'Failed to rate article' },
    );

    const [deleteLoading, triggerDelete] = useApiWithoutAutoExecute<void, object>(
        () => demeterNewsApi.deleteDemeterNewsArticle(props.article.demeterNewsArticleGuid),
        { successMessage: 'Article deleted', errorMessage: 'Failed to delete article' },
    );

    const updating = statusUpdating || ratingLoading || deleteLoading;
    const previousUpdatingReference = useRef(false);

    useEffect(() => {
        if (previousUpdatingReference.current && !updating) {
            props.onUpdated();
        }
        previousUpdatingReference.current = updating;
    });

    const handleStatusChange = (targetStatus: DemeterNewsArticleStatus) => {
        updateStatus({
            demeterNewsArticleGuid: props.article.demeterNewsArticleGuid,
            targetStatus,
        });
    };

    const handleRate = () => {
        triggerRate();
    };

    const handleDelete = () => {
        triggerDelete();
    };

    const getActionHandler = (action: ArticleAction) => {
        if (action === ArticleAction.Rate) {
            return handleRate;
        }
        if (action === ArticleAction.Delete) {
            return handleDelete;
        }
        return () => handleStatusChange(ACTION_STATUS_MAP[action]!);
    };

    return (
        <div className={styles.action_buttons}>
            {availableActions.map((action) => (
                <ActionsCellButton
                    key={action}
                    text={action}
                    handleClick={getActionHandler(action)}
                    disabled={updating}
                    track={{ eventCategory: 'AiNewsRating', eventAction: `Article${action}` }}
                />
            ))}
        </div>
    );
};

const makeTitleCellRenderer =
    (onSelect: (article: DemeterNewsArticleModel) => void) =>
    (row: DemeterNewsArticleModel): JSX.Element | string => {
        const title = row.articleName ?? row.demeterNewsArticleGuid;
        return (
            <div className={styles.link_cell} title={title}>
                <LinkButton
                    title={title}
                    type={LinkButtonType.White}
                    onClick={() => onSelect(row)}
                    testId={`ReviewTabArticleTitle-${row.demeterNewsArticleGuid}`}
                    track={{ eventCategory: 'AiNewsRating', eventAction: 'ArticleSelected' }}
                />
            </div>
        );
    };

const makeArticleActionsCellRenderer =
    (onUpdated: () => void) =>
    (row: DemeterNewsArticleModel): JSX.Element =>
        <ArticleActionsCell article={row} onUpdated={onUpdated} />;

const renderArticleSource = (row: DemeterNewsArticleModel): string => row.articleSource ?? '-';

const makeConfidenceCellRenderer =
    (ratingsMap: Map<string, DemeterNewsArticleRatingModel[]>) =>
    (row: DemeterNewsArticleModel): string => {
        const ratings = ratingsMap.get(row.demeterNewsArticleGuid);
        if (!ratings || ratings.length === 0) {
            return '-';
        }
        const maxConfidence = Math.max(...ratings.map((r) => r.confidenceScore ?? 0));
        return maxConfidence > 0 ? maxConfidence.toFixed(2) : '-';
    };

const ReviewTab: React.FC = () => {
    const [translations] = useLanguage();
    const [take, setTake] = useState(100);
    const [skip, setSkip] = useState(0);
    const [selectedArticle, setSelectedArticle] = useState<DemeterNewsArticleModel | null>(null);
    const [ratingsMap, setRatingsMap] = useState<Map<string, DemeterNewsArticleRatingModel[]>>(new Map());

    const [loading, refresh, listResponse, error] = useApiWithoutAutoExecute<void, ListDemeterNewsArticlesResponse>(
        () => demeterNewsApi.listDemeterNewsArticles(undefined, undefined, undefined, undefined, 'ArticleDate', SortDirection.Desc, take, skip),
        { showResponseErrorMessage: true, errorMessage: 'Failed to load articles' },
    );

    useEffect(() => {
        refresh();
    }, [take, skip]);

    const fetchRatings = useCallback(async (articleList: DemeterNewsArticleModel[]) => {
        if (articleList.length === 0) {
            setRatingsMap(new Map());
            return;
        }
        const results = await Promise.allSettled(articleList.map((a) => demeterNewsApi.listDemeterNewsArticleRatings(a.demeterNewsArticleGuid)));
        const newMap = new Map<string, DemeterNewsArticleRatingModel[]>();
        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                newMap.set(articleList[index].demeterNewsArticleGuid, result.value.data.rows ?? []);
            }
        });
        setRatingsMap(newMap);
    }, []);

    useEffect(() => {
        const articleList = listResponse?.rows ?? [];
        fetchRatings(articleList);
    }, [listResponse, fetchRatings]);

    const columns: TableHeader<DemeterNewsArticleModel>[] = useMemo(
        () => [
            {
                header: translations.words.title,
                minimumWidth: 250,
                cellRenderer: makeTitleCellRenderer((article) => setSelectedArticle(article)),
            },
            {
                header: translations.words.source,
                minimumWidth: 80,
                cellRenderer: renderArticleSource,
            },
            {
                header: translations.words.date,
                minimumWidth: 160,
                cellRenderer: formatArticleDate,
            },
            {
                header: translations.words.confidence,
                minimumWidth: 80,
                cellRenderer: makeConfidenceCellRenderer(ratingsMap),
            },
            {
                header: translations.words.status,
                minimumWidth: 120,
                cellRenderer: renderStatusBadge,
            },
            {
                header: translations.words.actions,
                minimumWidth: 240,
                cellRenderer: makeArticleActionsCellRenderer(refresh),
            },
        ],
        [refresh, ratingsMap, translations],
    );

    const articles = useMemo(() => listResponse?.rows ?? [], [listResponse]);
    const totalCount = listResponse?.totalCount ?? articles.length;

    if (selectedArticle) {
        return (
            <ArticleDetail
                article={selectedArticle}
                onBack={() => {
                    setSelectedArticle(null);
                    refresh();
                }}
            />
        );
    }

    return (
        <div className={styles.page_container} data-testid="AiNewsRatingReview">
            {loading && <TableSkeleton numberOfColumns={6} numberOfRows={10} />}
            {error && <div className={styles.error_message}>{error}</div>}

            {!loading && !error && (
                <>
                    <div className={styles.meta_count}>
                        {translations.words.total}: {totalCount}
                    </div>
                    <PaginationTableWrapper
                        apiListResponse={{ totalCount, take, skip } as ApiListResponse}
                        showPagination
                        handlePagination={({ skip: pageSkip }: ApiListResponse) => setSkip(pageSkip ?? 0)}
                        handleTakeCountUpdate={({ take: pageTake }: ApiListResponse) => {
                            setTake(pageTake ?? 100);
                            setSkip(0);
                        }}
                        track={{ eventCategory: 'AiNewsRating', eventAction: 'ReviewPageChanged' }}
                    >
                        <Table tableHeaders={columns} tableData={articles} tableStyle="alternatingRows" />
                    </PaginationTableWrapper>
                    {articles.length === 0 && <div className={styles.empty_state}>No articles found</div>}
                </>
            )}
        </div>
    );
};

export default ReviewTab;
